/**
 * BpmnNodeCard - Cartão único usado por TODOS os blocos (menos Início e Fim)
 *
 * Padrão Monday/Bitrix: todo bloco tem exatamente o mesmo formato de cartão —
 * selo colorido com o ícone do tipo, nome do bloco e um resumo curto. O que
 * muda de um bloco para o outro é apenas o ícone e a cor semântica.
 *
 * Variações de comportamento (mesmo cartão):
 *  - Tarefa humana de aprovação: âncoras "Aprovado"/"Reprovado" nas laterais.
 *  - Decisão e Execução paralela: botão "+" no canto direito DENTRO do cartão,
 *    que abre a lista de blocos e já cria a ramificação com o bloco escolhido;
 *    uma âncora por saída na base.
 *  - Junção: sem "+" (só recebe linhas); mostra a política no resumo.
 *
 * Início e Fim continuam desenhados pelo `BpmnEventNode` (círculos).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-04 v1.1.0 - "+" do cartão de decisão/paralelo passa a abrir
 *          a lista de blocos (NodePickerPopover) e cria diretamente o bloco
 *          escolhido na nova ramificação, sem bloco temporário intermediário.
 * RELEASE: 2026-09-04 v1.0.0 - Unificação visual dos blocos: substitui
 *          `BpmnTaskNode` e `BpmnGatewayNode` e absorve os eventos
 *          intermediários, exigência e temporizador.
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';
import { AlertTriangle, Check, X, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import type { WorkflowActionConfig } from '@/hooks/useWorkflowDesigner';
import { ROTULO_DA_FORMA, ESTILO_DA_FORMA, ICONE_DA_FORMA, type BpmnShape } from './bpmnShapes';
import { BpmnAppendButton } from './BpmnAppendButton';
import { NodePickerPopover } from './NodePickerPopover';
import type { WorkflowPaletteItem } from '../workflowNodeMeta';

/** Uma saída (linha) do bloco — usada apenas para gerar as âncoras no mapa */
export interface SaidaDoBloco {
  /** id da aresta — também é o id da âncora (handle) usada no mapa */
  id: string;
  rotulo: string;
  /** Nome do bloco de destino (ou null quando ainda é um destino temporário) */
  destino: string | null;
  padrao: boolean;
  /** true quando a saída não tem regra nem evento (só o caminho padrão pode) */
  semRegra: boolean;
}

export interface BpmnNodeCardData extends Record<string, unknown> {
  label: string;
  shape: BpmnShape;
  resumo?: string | null;
  actions?: WorkflowActionConfig[];
  actionLabels?: Record<string, string>;
  alerta?: string | null;
  podeEditar?: boolean;
  semSaida?: boolean;
  /** Número do passo exibido à esquerda do cartão */
  passo?: number;
  /** Tarefa humana configurada como aprovação (duas saídas nomeadas) */
  aprovacao?: boolean;
  caminhoAprovado?: string | null;
  caminhoReprovado?: string | null;
  /** Saídas configuradas (uma âncora por caminho) — decisão / execução paralela */
  saidasLista?: SaidaDoBloco[];
  /** Cria uma ramificação nova a partir deste bloco com o bloco escolhido na lista */
  onAdicionarSaida?: (nodeId: string, item: WorkflowPaletteItem) => void;
  /** `handle` indica a âncora usada ("aprovado" | "reprovado") */
  onAdicionar?: (nodeId: string, item: WorkflowPaletteItem, handle?: string | null) => void;
}

export function BpmnNodeCard({ id, data, selected }: NodeProps) {
  const node = data as BpmnNodeCardData;
  const estilo = ESTILO_DA_FORMA[node.shape];
  const Icon = ICONE_DA_FORMA[node.shape];

  const aprovacao = !!node.aprovacao;
  // Blocos que ramificam: criam saídas pelo "+" interno do cartão
  const ramifica = node.shape === 'gateway_condicao' || node.shape === 'gateway_exclusivo' || node.shape === 'gateway_paralelo';
  const juncao = node.shape === 'gateway_juncao';
  const saidas = node.saidasLista ?? [];
  const acoes = node.actions ?? [];

  return (
    <div className="flex w-[300px] flex-col items-center">
      <div className="flex w-full items-start gap-2">
        {typeof node.passo === 'number' && (
          <span className="mt-4 w-4 shrink-0 text-right text-[11px] font-medium text-muted-foreground">
            {node.passo}
          </span>
        )}

        <div className="relative min-w-0 flex-1">
          <div
            className={`rounded-xl border bg-card shadow-sm transition-all ${
              selected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40'
            }`}
          >
            <Handle type="target" position={Position.Top} className="!h-2.5 !w-2.5" />

            <div className="flex items-center gap-3 px-3 py-2.5">
              {/* Selo colorido: única diferença visual entre os tipos de bloco */}
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${estilo.selo}`}>
                <Icon className="h-4 w-4" />
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{node.label}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {node.resumo || ROTULO_DA_FORMA[node.shape]}
                </p>
              </div>

              {/* "+" dentro do cartão: abre a lista de blocos e já cria a
                  ramificação com o bloco escolhido (sem bloco temporário) */}
              {ramifica && node.podeEditar && (
                <NodePickerPopover
                  onSelect={(item) => node.onAdicionarSaida?.(id, item)}
                  trigger={
                    <button
                      type="button"
                      aria-label="Adicionar ramificação"
                      title="Adicionar ramificação — escolha o próximo bloco"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="nodrag flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/60 bg-card text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  }
                />
              )}
            </div>

            {(acoes.length > 0 || node.alerta || aprovacao) && (
              <div className="space-y-1.5 border-t px-3 py-2">
                {acoes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {acoes.slice(0, 3).map((acao, index) => (
                      <Badge key={`${acao.action_code}-${index}`} variant="secondary" className="text-[10px]">
                        {node.actionLabels?.[acao.action_code] || acao.action_code}
                      </Badge>
                    ))}
                    {acoes.length > 3 && (
                      <Badge variant="outline" className="text-[10px]">
                        +{acoes.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {aprovacao && (
                  <div className="flex flex-col gap-1">
                    <span
                      className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-medium ${
                        node.caminhoAprovado ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Check className="h-3 w-3 shrink-0" />
                      Aprovado
                      <span className="truncate font-normal">→ {node.caminhoAprovado ?? 'sem caminho'}</span>
                    </span>
                    <span
                      className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-medium ${
                        node.caminhoReprovado ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <X className="h-3 w-3 shrink-0" />
                      Reprovado
                      <span className="truncate font-normal">→ {node.caminhoReprovado ?? 'sem caminho'}</span>
                    </span>
                  </div>
                )}

                {node.alerta && (
                  <p className="flex items-center gap-1 text-[11px] text-warning">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> {node.alerta}
                  </p>
                )}
              </div>
            )}

            {/* ------------------------------------------------------------
                ÂNCORAS DE SAÍDA
            ------------------------------------------------------------- */}
            {aprovacao ? (
              <>
                <Handle
                  id="aprovado"
                  type="source"
                  position={Position.Left}
                  className="!h-2.5 !w-2.5 !-translate-x-1/2 !bg-success"
                />
                <Handle
                  id="reprovado"
                  type="source"
                  position={Position.Right}
                  className="!h-2.5 !w-2.5 !translate-x-1/2 !bg-destructive"
                />
              </>
            ) : (
              <>
                {/* Uma âncora por saída configurada (id = id da aresta) */}
                {ramifica &&
                  saidas.map((s, indice) => (
                    <Handle
                      key={s.id}
                      id={s.id}
                      type="source"
                      position={Position.Bottom}
                      style={{ left: `${((indice + 1) / (saidas.length + 1)) * 100}%` }}
                      className={`!h-2.5 !w-2.5 ${s.padrao ? '!bg-muted-foreground' : '!bg-primary'}`}
                    />
                  ))}

                {/* Âncora livre (ligações arrastadas à mão) */}
                <Handle
                  type="source"
                  position={Position.Bottom}
                  style={ramifica ? { left: 'calc(100% - 12px)' } : undefined}
                  className="!h-2.5 !w-2.5"
                />
              </>
            )}
          </div>

          {/* Botões "+" laterais da tarefa humana de aprovação */}
          {node.podeEditar && aprovacao && (
            <>
              {!node.caminhoAprovado && (
                <span className="absolute left-0 top-1/2 flex -translate-x-full -translate-y-1/2 items-center gap-1 pr-1 text-[10px] text-success">
                  <BpmnAppendButton onSelect={(item) => node.onAdicionar?.(id, item, 'aprovado')} />
                  Aprovado
                </span>
              )}
              {!node.caminhoReprovado && (
                <span className="absolute right-0 top-1/2 flex translate-x-full -translate-y-1/2 items-center gap-1 pl-1 text-[10px] text-destructive">
                  <BpmnAppendButton onSelect={(item) => node.onAdicionar?.(id, item, 'reprovado')} />
                  Reprovado
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Botão "+" inferior: blocos comuns sem saída (junção e ramificação ficam de fora) */}
      {node.podeEditar && !aprovacao && !ramifica && !juncao && node.semSaida && (
        <div className="mt-1.5">
          <BpmnAppendButton onSelect={(item) => node.onAdicionar?.(id, item)} />
        </div>
      )}
    </div>
  );
}
